import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import apiService from '../services/api';
import type { PerformanceDashboard } from '../types/performanceMonitoring';

function formatMs(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${ms.toFixed(0)} ms`;
}

function StatChip({ label, value }: { label: string; value: string | number }) {
  return (
    <Chip label={`${label} : ${value}`} variant="outlined" sx={{ mr: 1, mb: 1 }} />
  );
}

const PerformanceMonitoringPage: React.FC = () => {
  const [searchQ, setSearchQ] = useState('');
  const [routeFilter, setRouteFilter] = useState('');

  const { data: config } = useQuery({
    queryKey: ['perf-monitoring-config'],
    queryFn: () => apiService.getPerformanceMonitoringConfig(),
    staleTime: 10 * 60_000,
  });

  const {
    data: dashboard,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['perf-monitoring-dashboard'],
    queryFn: () => apiService.getPerformanceDashboard(),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 60_000,
  });

  const handleExport = async () => {
    await apiService.downloadPerformanceLogsExport({
      q: searchQ || undefined,
      route: routeFilter || undefined,
    });
  };

  if (isLoading && !dashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const err = error as { response?: { data?: { error?: string } } } | null;
  const d = dashboard as PerformanceDashboard | undefined;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" gutterBottom>
            Monitoring & Performance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Routes lentes, SQL, HTTP externes, images, erreurs et mémoire — instrumentation légère pour OVH.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void refetch()} disabled={isFetching}>
            Actualiser
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => void handleExport()}>
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {!config?.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          MONITORING_ENABLED=false sur l&apos;API — activez la variable d&apos;environnement pour collecter des données.
        </Alert>
      )}

      {config?.enabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Seuils : {config.thresholds.duration_ms} ms · {config.thresholds.memory_mb} Mo · {config.thresholds.sql_queries} SQL ·{' '}
          {config.thresholds.http_calls} HTTP · {config.thresholds.image_ms} ms images · Rétention {config.retention_days} j
          {d?.unacknowledged_alerts ? ` · ${d.unacknowledged_alerts} alerte(s) ouverte(s)` : ''}
        </Alert>
      )}

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err.response?.data?.error ?? 'Impossible de charger le dashboard performance.'}
        </Alert>
      )}

      {d && (
        <Grid container spacing={2}>
          {/* Carte 1 — Temps de réponse */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Temps de réponse
                </Typography>
                <StatChip label="5 min" value={formatMs(d.response_time.avg_5m)} />
                <StatChip label="1 h" value={formatMs(d.response_time.avg_1h)} />
                <StatChip label="24 h" value={formatMs(d.response_time.avg_24h)} />
                <Box sx={{ height: 220, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={d.response_time.series_24h}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={(v) => `${v} ms`} />
                      <Tooltip formatter={(v) => formatMs(Number(v ?? 0))} />
                      <Legend />
                      <Line type="monotone" dataKey="avg_ms" name="Moyenne" stroke="#2563eb" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Carte 7 — Mémoire */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Mémoire
                </Typography>
                <StatChip label="Moyenne 24 h" value={`${d.memory.avg_24h} Mo`} />
                <StatChip label="Pic 24 h" value={`${d.memory.max_24h} Mo`} />
                <Box sx={{ height: 220, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={d.memory.series_24h}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={(v) => `${v} Mo`} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="avg_mb" name="Moyenne" stroke="#7c3aed" dot={false} />
                      <Line type="monotone" dataKey="max_mb" name="Pic" stroke="#dc2626" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Carte 2 — Routes lentes */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Routes les plus lentes
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Route</TableCell>
                      <TableCell align="right">Appels</TableCell>
                      <TableCell align="right">Moyenne</TableCell>
                      <TableCell align="right">Max</TableCell>
                      <TableCell align="right">Min</TableCell>
                      <TableCell>Dernière exécution</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {d.slow_routes.map((r) => (
                      <TableRow key={r.route}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.route}</TableCell>
                        <TableCell align="right">{r.call_count}</TableCell>
                        <TableCell align="right">{formatMs(r.avg_ms)}</TableCell>
                        <TableCell align="right">{formatMs(r.max_ms)}</TableCell>
                        <TableCell align="right">{formatMs(r.min_ms)}</TableCell>
                        <TableCell>{r.last_execution}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Carte 3 — SQL */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Requêtes SQL
                </Typography>
                <StatChip label="Moyenne / requête" value={d.sql.avg_queries} />
                <StatChip label="Temps SQL moyen" value={formatMs(d.sql.avg_sql_ms)} />
                <StatChip label="Requêtes &gt; 100 ms" value={d.sql.slow_queries_over_100ms} />
                {d.sql.slowest && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Requête la plus lente ({formatMs(d.sql.slowest.duration_ms)})</Typography>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {d.sql.slowest.sql}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Carte 4 — HTTP externes */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Appels HTTP externes
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Appels</TableCell>
                      <TableCell align="right">Moy.</TableCell>
                      <TableCell align="right">Max</TableCell>
                      <TableCell align="right">Échecs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {d.http_external.map((h) => (
                      <TableRow key={`${h.service}-${h.host}`}>
                        <TableCell>
                          <Typography variant="body2">{h.service}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {h.host}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{h.call_count}</TableCell>
                        <TableCell align="right">{formatMs(h.avg_ms)}</TableCell>
                        <TableCell align="right">{formatMs(h.max_ms)}</TableCell>
                        <TableCell align="right">{h.failures}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Carte 5 — Images */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Traitements d&apos;images
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Opération</TableCell>
                      <TableCell align="right">Moy.</TableCell>
                      <TableCell align="right">Pic mémoire</TableCell>
                      <TableCell align="right">Dimensions max</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {d.images.map((img) => (
                      <TableRow key={img.operation}>
                        <TableCell>{img.operation}</TableCell>
                        <TableCell align="right">{formatMs(img.avg_ms)}</TableCell>
                        <TableCell align="right">{img.max_memory_mb} Mo</TableCell>
                        <TableCell align="right">
                          {img.max_width > 0 ? `${img.max_width}×${img.max_height}` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Carte 6 — Erreurs */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Erreurs
                </Typography>
                <StatChip label="HTTP 500" value={d.errors.http_500} />
                <StatChip label="HTTP 502" value={d.errors.http_502} />
                <StatChip label="5xx" value={d.errors.http_5xx} />
                <StatChip label="Exceptions" value={d.errors.exceptions} />
                <StatChip label="HTTP externes" value={d.errors.http_external_failures} />
                <Table size="small" sx={{ mt: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Route</TableCell>
                      <TableCell>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(d.errors.recent as PerformanceDashboard['top_expensive']).slice(0, 8).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.created_at}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{row.route}</TableCell>
                        <TableCell>{row.status_code}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Carte 8 — Top 20 coûteuses */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <TextField size="small" label="Recherche" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
                  <TextField size="small" label="Route" value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)} />
                </Stack>
                <Typography variant="h6" gutterBottom>
                  Top 20 requêtes les plus coûteuses
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Route</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">SQL</TableCell>
                      <TableCell align="right">HTTP</TableCell>
                      <TableCell align="right">Images</TableCell>
                      <TableCell align="right">Mémoire</TableCell>
                      <TableCell align="right">User</TableCell>
                      <TableCell>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {d.top_expensive.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.created_at}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{row.route}</TableCell>
                        <TableCell align="right">{formatMs(row.duration_ms)}</TableCell>
                        <TableCell align="right">
                          {row.sql_queries} / {formatMs(row.sql_duration_ms)}
                        </TableCell>
                        <TableCell align="right">
                          {row.http_calls} / {formatMs(row.http_duration_ms)}
                        </TableCell>
                        <TableCell align="right">{formatMs(row.image_duration_ms)}</TableCell>
                        <TableCell align="right">{row.memory_peak_mb} Mo</TableCell>
                        <TableCell align="right">{row.user_id ?? '—'}</TableCell>
                        <TableCell>{row.status_code}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default PerformanceMonitoringPage;
